import { useCallback } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import fetch from 'features/common/fetch';
import {
	loadMore,
	loadPage,
	reload,
	selectCommunicationGroup,
	sortRecords,
} from 'features/views/contactHistory/actions';
import { getCommunicationGroups, getSelectedGroupId, getSort } from 'features/views/contactHistory/selectors';

import CommunicationOverviewTableRow from './CommunicationOverviewTableRow';

export const CommunicationOverviewTable = () => {
	const dispatch = useDispatch();
	const communicationGroups = useSelector(getCommunicationGroups);
	const selectedGroupId = useSelector(getSelectedGroupId);
	const sort = useSelector(getSort);

	const hasRecords = !isEmpty(communicationGroups);

	const handleReload = useCallback(() => {
		dispatch(reload());
	}, [dispatch]);

	const handleLoadMore = useCallback(() => {
		dispatch(loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(loadPage(page - 1));
		},
		[dispatch],
	);

	const handleSort = useCallback(
		(newSort) => {
			dispatch(sortRecords(newSort));
		},
		[dispatch],
	);

	const handleSelect = useCallback(
		(id) => () => {
			dispatch(selectCommunicationGroup(id));
		},
		[dispatch],
	);

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.communicationHistory} onReload={handleReload}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>
							<SortCell
								hasRecords={hasRecords}
								sortBy="min_communication_start"
								sort={sort}
								onSort={handleSort}
							>
								{getLabelMessage('contactHistory:date')}
							</SortCell>
						</TableCell>
						<TableCell>{getLabelMessage('contactHistory:client')}</TableCell>
						<TableCell>{getLabelMessage('contactHistory:contactType')}</TableCell>
						<TableCell>
							<div>{getLabelMessage('contactHistory:worker')}</div>
							<div>{getLabelMessage('contactHistory:distributionChannel')}</div>
						</TableCell>
						<TableCell>
							<div>{getLabelMessage('contactHistory:businessService')}</div>
							<div>{getLabelMessage('contactHistory:blocking')}</div>
						</TableCell>
						<TableCell>
							<div>{getLabelMessage('contactHistory:cancelRequest')}</div>
							<div>{getLabelMessage('contactHistory:services')}</div>
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{!hasRecords ? (
						<TableHasNoResults />
					) : (
						communicationGroups.map((communicationGroup) => (
							<TableRow
								key={communicationGroup.id}
								onClick={handleSelect(communicationGroup.id)}
								hover={!(selectedGroupId === communicationGroup.id)}
								sx={{
									backgroundColor:
										selectedGroupId === communicationGroup.id ? 'primary.shades.8p' : 'default',
								}}
							>
								<CommunicationOverviewTableRow communicationGroup={communicationGroup} />
							</TableRow>
						))
					)}
				</TableBody>
				<PaginationRow
					fetchStorePath={fetch.constants.urls.communicationHistory}
					colSpan={100}
					onLoadNext={handleLoadMore}
					onPaginationClick={handlePaginationClick}
				/>
			</Table>
		</ComponentLoader>
	);
};

export default CommunicationOverviewTable;
